import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import AjustaTamanhoMapa from './AjustaTamanhoMapa';
import { useFecharComEsc } from '../../utils/useFecharComEsc';

// Componente GENÉRICO de desenho/edição de geometria sobre um mapa — não
// sabe nada sobre locais de pesca nem qualquer outro domínio, só recebe uma
// geometria GeoJSON opcional e devolve (via onMudou) a geometria resultante
// de desenhar/editar/apagar. Pensado pra ser plugável em qualquer tela que
// precise de um point/linha/polígono num mapa (ex: uma futura tela de
// territórios de apicultura), sem reescrever a parte de mapa.
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Tradução da barra de ferramentas do Leaflet.Draw — resto da interface é
// todo em pt-BR, deixar a barra em inglês destoaria. IMPORTANTE: mescla nos
// textos padrão (L.drawLocal já populado pelo import 'leaflet-draw' acima)
// em vez de SUBSTITUIR o objeto inteiro — o toolbar do plugin instancia
// TODOS os handlers internamente (Rectangle, Circle, etc.), mesmo os que a
// gente nunca habilita/mostra (só polygon/polyline/marker), e cada um lê
// seu próprio texto de L.drawLocal no construtor. Substituir o objeto
// inteiro apaga esses textos "invisíveis" e quebra com "Cannot read
// properties of undefined (reading 'tooltip')" ao abrir QUALQUER mapa de
// desenho — testado ao vivo, esse foi exatamente o erro.
const drawLocal = (L as any).drawLocal;
Object.assign(drawLocal.draw.toolbar.actions, { title: 'Cancelar desenho', text: 'Cancelar' });
Object.assign(drawLocal.draw.toolbar.finish, { title: 'Concluir desenho', text: 'Concluir' });
Object.assign(drawLocal.draw.toolbar.undo, { title: 'Apagar último ponto', text: 'Apagar último ponto' });
Object.assign(drawLocal.draw.toolbar.buttons, {
  polyline: 'Desenhar uma linha', polygon: 'Desenhar um polígono', marker: 'Marcar um ponto',
});
Object.assign(drawLocal.draw.handlers.marker.tooltip, { start: 'Clique no mapa pra marcar o ponto.' });
Object.assign(drawLocal.draw.handlers.polygon.tooltip, {
  start: 'Clique pra começar a desenhar o polígono.',
  cont: 'Clique pra continuar desenhando.',
  end: 'Clique no primeiro ponto pra fechar o polígono.',
});
drawLocal.draw.handlers.polyline.error = '<strong>Erro:</strong> as bordas da linha não podem se cruzar!';
Object.assign(drawLocal.draw.handlers.polyline.tooltip, {
  start: 'Clique pra começar a desenhar a linha.',
  cont: 'Clique pra continuar desenhando.',
  end: 'Clique no último ponto pra terminar a linha.',
});
Object.assign(drawLocal.edit.toolbar.actions.save, { title: 'Salvar alterações', text: 'Salvar' });
Object.assign(drawLocal.edit.toolbar.actions.cancel, { title: 'Cancelar edição, descarta as alterações', text: 'Cancelar' });
Object.assign(drawLocal.edit.toolbar.actions.clearAll, { title: 'Apagar tudo', text: 'Apagar tudo' });
Object.assign(drawLocal.edit.toolbar.buttons, {
  edit: 'Editar geometria', editDisabled: 'Nada pra editar',
  remove: 'Apagar geometria', removeDisabled: 'Nada pra apagar',
});
Object.assign(drawLocal.edit.handlers.edit.tooltip, { text: 'Arraste os pontos pra editar.', subtext: 'Clique em cancelar pra desfazer.' });
Object.assign(drawLocal.edit.handlers.remove.tooltip, { text: 'Clique numa geometria pra apagar.' });

export type FerramentaDesenho = 'marker' | 'polyline' | 'polygon';

interface ControlesDesenhoProps {
  geometriaInicial?: any;
  ferramentas: FerramentaDesenho[];
  onMudou: (geometria: any | null) => void;
}

function ControlesDesenho({ geometriaInicial, ferramentas, onMudou }: ControlesDesenhoProps) {
  const map = useMap();
  // Ref (não state) — o MESMO FeatureGroup sobrevive a trocas de ferramenta
  // (ex: usuário muda o tipo do local no formulário), então um desenho já
  // feito não se perde só por isso.
  const featureGroupRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const carregouInicialRef = useRef(false);

  useEffect(() => {
    const featureGroup = featureGroupRef.current;
    map.addLayer(featureGroup);

    if (!carregouInicialRef.current && geometriaInicial) {
      carregouInicialRef.current = true;
      L.geoJSON(geometriaInicial).eachLayer((camada) => featureGroup.addLayer(camada));
      const bounds = featureGroup.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [20, 20] });
    }

    const opcoesDesenho: any = { polygon: false, polyline: false, marker: false, circle: false, circlemarker: false, rectangle: false };
    if (ferramentas.includes('polygon')) opcoesDesenho.polygon = { allowIntersection: false, showArea: true };
    if (ferramentas.includes('polyline')) opcoesDesenho.polyline = {};
    if (ferramentas.includes('marker')) opcoesDesenho.marker = {};

    const controle = new (L.Control as any).Draw({
      position: 'topright',
      draw: opcoesDesenho,
      edit: { featureGroup, remove: true },
    });
    map.addControl(controle);

    const emitirGeometriaAtual = () => {
      if (featureGroup.getLayers().length === 0) {
        onMudou(null);
        return;
      }
      const geojson = featureGroup.toGeoJSON() as any;
      const feature = geojson.type === 'FeatureCollection' ? geojson.features[0] : geojson;
      onMudou(feature.geometry || feature);
    };

    const aoCriar = (e: any) => {
      // Um nó só tem UMA geometria — desenhar de novo SUBSTITUI a anterior
      // em vez de acumular (evita ficar com formas concorrentes pro mesmo
      // local sem querer).
      featureGroup.clearLayers();
      featureGroup.addLayer(e.layer);
      emitirGeometriaAtual();
    };

    map.on((L as any).Draw.Event.CREATED, aoCriar);
    map.on((L as any).Draw.Event.EDITED, emitirGeometriaAtual);
    map.on((L as any).Draw.Event.DELETED, emitirGeometriaAtual);

    return () => {
      map.off((L as any).Draw.Event.CREATED, aoCriar);
      map.off((L as any).Draw.Event.EDITED, emitirGeometriaAtual);
      map.off((L as any).Draw.Event.DELETED, emitirGeometriaAtual);
      map.removeControl(controle);
      map.removeLayer(featureGroup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, ferramentas.join(',')]);

  return null;
}

function centroDeGeometria(geometria: any): [number, number] | null {
  if (!geometria) return null;
  try {
    const bounds = L.geoJSON(geometria).getBounds();
    if (!bounds.isValid()) return null;
    const centro = bounds.getCenter();
    return [centro.lat, centro.lng];
  } catch {
    return null;
  }
}

interface DesenhoGeometriaMapaProps {
  /** Geometria GeoJSON já existente (Point/LineString/Polygon) — carregada no mapa pra edição. */
  geometriaInicial?: any;
  /** Quais ferramentas de desenho ficam disponíveis na barra do mapa. */
  ferramentas: FerramentaDesenho[];
  /** Chamado com a geometria GeoJSON resultante a cada desenho/edição, ou `null` quando apagada. */
  onMudou: (geometria: any | null) => void;
  altura?: string;
  centroPadrao?: [number, number];
  zoomPadrao?: number;
}

const DesenhoGeometriaMapa: React.FC<DesenhoGeometriaMapaProps> = ({
  geometriaInicial,
  ferramentas,
  onMudou,
  altura = '260px',
  centroPadrao = [-22.9, -48.4],
  zoomPadrao = 7,
}) => {
  const centroInicial = centroDeGeometria(geometriaInicial);
  const centro = centroInicial || centroPadrao;
  const zoom = centroInicial ? 12 : zoomPadrao;

  // O mapa de marcar ponto/desenhar geometria ficava preso à altura fixa
  // do formulário (ex: 260px dentro do modal "Editar Local") — pequeno
  // demais pra marcar um ponto com precisão. Entrar/sair da tela cheia
  // remonta o MapContainer (a árvore de containers ao redor é diferente
  // nos dois modos), mas isso não perde geometria já desenhada: ela volta
  // a partir de `geometriaInicial`, que o pai mantém atualizada a cada
  // onMudou. Só um desenho EM ANDAMENTO (ainda não concluído) seria
  // perdido ao trocar de modo no meio do desenho.
  const [telaCheia, setTelaCheia] = useState(false);
  useFecharComEsc(telaCheia, () => setTelaCheia(false));

  const conteudoMapa = (
    <MapContainer center={centro} zoom={zoom} style={{ width: '100%', height: '100%' }}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <ControlesDesenho geometriaInicial={geometriaInicial} ferramentas={ferramentas} onMudou={onMudou} />
      <AjustaTamanhoMapa gatilho={telaCheia} />
    </MapContainer>
  );

  // top-right é onde o Leaflet.Draw coloca a barra de ferramentas
  // (ControlesDesenho usa position: 'topright') — um botão nosso ali por
  // cima brigava com ela (control do Leaflet e nosso <button> disputando o
  // mesmo canto/z-index, cliques indo pro elemento errado). bottom-left
  // fica livre: zoom é topleft, draw é topright, atribuição é bottomright.
  const botaoTelaCheia = (
    <button
      type="button"
      title={telaCheia ? 'Sair da tela cheia' : 'Expandir o mapa em tela cheia'}
      onClick={() => setTelaCheia((v) => !v)}
      style={{
        position: 'absolute', bottom: '10px', left: '10px', zIndex: 1000,
        width: '34px', height: '34px', borderRadius: '6px', border: '2px solid rgba(0,0,0,0.2)',
        background: '#fff', color: '#333', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {telaCheia ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
    </button>
  );

  if (telaCheia) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ position: 'relative', width: '100%', height: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
          {conteudoMapa}
          {botaoTelaCheia}
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', height: altura, borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
      {conteudoMapa}
      {botaoTelaCheia}
    </div>
  );
};

export default DesenhoGeometriaMapa;
